/**
 * How the TV app restarts itself, without falling off the host it lives in.
 *
 * THE BUG THIS EXISTS FOR (measured on a Samsung panel, 2026-08-10).
 *
 * Pairing succeeded: the code was redeemed, a room was picked, the server
 * registered the screen, and the CMS showed it. Then the TV bounced straight
 * back to the six-digit code screen.
 *
 * The cause was the last line of the pairing flow, `location.assign("/")`. On
 * Android and on the web that is correct and harmless. On Samsung the app is
 * TRANSCLUDED - the WGT bootloader fetches the bundle and runs it inside the
 * widget's own document, precisely so the Tizen APIs stay alive (TIZEN-4). So
 * `/` is not "the app" there; it is a different origin. Navigating to it:
 *
 *   1. leaves the widget's localStorage bucket, where `setBinding()` had just
 *      written the screen token a few milliseconds earlier - so the app boots,
 *      finds no binding, and shows the pairing screen. The credential is not
 *      lost, it is simply somewhere the new document cannot see.
 *   2. throws away `window.tizen` and every device capability with it.
 *
 * Both failures are silent, and the visible symptom - "pairing didn't stick" -
 * points at storage, which is the one thing that was working.
 *
 * `location.reload()` is safe on every platform: it re-runs the CURRENT
 * document. On Samsung that re-runs the bootloader, which re-transcludes the
 * app, which reads the binding from the bucket it was written to. On Android
 * and web it is an ordinary reload of the same page.
 */

import { noteBoot, readBootTrace } from "./bootTrace";
import { hostSearch } from "./hostQuery";

/**
 * Is the app running inside a host that transcluded it rather than serving it?
 *
 * Keyed on the host override rather than on the platform: a bootloader that had
 * to INJECT the query is exactly a bootloader whose document is not the app's
 * own URL. Capability, not platform - the same rule the whole bridge follows.
 *
 * ⚠️ THE `!location.search` CLAUSE THAT USED TO BE HERE RE-ARMED THE VERY BUG
 * THIS MODULE EXISTS TO PREVENT (found on hasselbacken/302, 2026-09-09).
 *
 * The old test was `typeof __FOYER_SEARCH__ === "string" && !location.search`,
 * the second half defending against "a stale global lingering in a real
 * browser". Nothing in a real browser ever sets that global - only a bootloader
 * does - so the clause defended against nothing, and it answered a DIFFERENT
 * question from the one asked here: which query wins (that is `hostSearch`'s
 * job, and it is right to prefer the URL) is not the same as which document we
 * are running in.
 *
 * What made it fatal: `tizen/index.html` follows its `__FOYER_SEARCH__` write
 * with a best-effort `history.replaceState(null, "", q)`, commented "harmless
 * where it works". On a panel whose firmware HONOURS it, `location.search`
 * becomes `?tv=1` - so the second half went false, this returned false on a
 * genuinely transcluded widget, and `restartApp()` fell through to
 * `location.assign("/")`: the exact line described above, with all of its
 * consequences. Observed as a Tizen panel that redeemed a pairing code
 * successfully (server issued the token, CMS showed the screen) and then sat on
 * the code screen forever, reporting "Unknown TV" because `window.tizen` had
 * gone with the navigation, and unable to self-recover because the serial-based
 * re-attach retry is gated on a serial that same navigation took away.
 *
 * So: the global's PRESENCE is the signal, full stop. The protocol check below
 * is belt and braces for a bootloader that forgets to set it - a document not
 * served over http(s) has no meaningful "/" to navigate to either way.
 */
export function isTranscludedHost(): boolean {
  try {
    if (typeof window.__FOYER_SEARCH__ === "string") return true;
    const protocol = window.location.protocol;
    return protocol !== "http:" && protocol !== "https:";
  } catch {
    return false;
  }
}

const URL_REPAIRED_CRUMB = "host_url_repaired";

/**
 * Put a transcluded widget back on the URL it was actually loaded at.
 *
 * ⚠️ WHY `reload()` ALONE WAS NOT ENOUGH (hasselbacken/302, 2026-09-15).
 *
 * b5d8f43 fixed the detector above and the panel still bounced to "Unknown TV"
 * after a successful redeem - same signature, empty crumb trail and no Trace
 * line. The b5d8f43 diagnosis was that the bootloader's
 * `history.replaceState(null, "", "?tv=1")` had TAKEN on that firmware. What it
 * missed is the line before it: the bootloader appends
 * `<base href="https://foyer.nenda.com/">` first, and `replaceState` resolves a
 * relative URL against the document's BASE URL. So "took" means the widget's
 * URL now reads `https://foyer.nenda.com/?tv=1`, and `location.reload()`
 * reloads the CURRENT URL - a navigation to the remote origin, with every
 * consequence described at the top of this file. Verified in Chromium
 * (2026-09-15): the SecurityError on a desktop file:// page names the
 * base-resolved URL, and on a host that allows the rewrite, `reload()` loads the
 * rewritten URL instead of the document that ran.
 *
 * Every WGT up to 0.0.8 has that call, and removing it from the bootloader only
 * reaches a panel on a reinstall. So the app undoes it: the Navigation Timing
 * entry keeps the URL the document was LOADED at (`replaceState` does not
 * touch it), and replacing back to the document's own URL is same-origin by
 * definition, so any host that allowed the rewrite allows the repair.
 *
 * Called once at boot (main.tsx) so every raw `location.reload()` in the app is
 * safe, and again inside the restart helpers as defence in depth. A no-op off a
 * transcluded host (a real browser's URL is the truth, SPA routes included),
 * when nothing was rewritten, or where Navigation Timing is missing - which is
 * exactly today's behaviour. Returns whether it changed the URL.
 */
export function repairHostDocumentUrl(): boolean {
  try {
    if (!isTranscludedHost()) return false;
    const entry = window.performance?.getEntriesByType?.("navigation")?.[0];
    const loadedAt = entry?.name;
    if (!loadedAt) return false;
    const current = window.location.href;
    if (current === loadedAt) return false;
    window.history.replaceState(window.history.state, "", loadedAt);
    recordRepair(current);
    return true;
  } catch {
    return false;
  }
}

/**
 * One crumb per run of repairs, not one per boot: a panel on WGT 0.0.8 repairs
 * on EVERY boot, and six identical crumbs would push out the ones that explain
 * why a TV is sitting on the pairing screen. It is the on-hardware proof this
 * rewrite happens at all, so it must land on the Trace line.
 */
function recordRepair(rewrittenUrl: string): void {
  try {
    const trail = readBootTrace();
    if (trail[trail.length - 1]?.at === URL_REPAIRED_CRUMB) return;
    let origin = "unparsed";
    try {
      origin = new URL(rewrittenUrl).origin;
    } catch {
      /* keep "unparsed" */
    }
    noteBoot(URL_REPAIRED_CRUMB, origin);
  } catch {
    /* diagnostics never break the boot */
  }
}

/**
 * Restart the app at its root.
 *
 * Use this instead of `location.assign("/")` or `location.replace("/")`
 * anywhere the intent is "start the app again", so a transcluded host is not
 * navigated out from under itself.
 */
export function restartApp(): void {
  try {
    if (isTranscludedHost()) {
      repairHostDocumentUrl();
      window.location.reload();
      return;
    }
    window.location.assign("/");
  } catch {
    // A host that refuses both is beyond our help, but it must not throw into
    // a caller that was mid-pairing.
  }
}

/**
 * Restart at the root having deliberately DISCARDED local state (the `?reset=1`
 * path). Same origin rule as `restartApp`; `replace` so the reset URL does not
 * sit in history and re-fire on a Back press.
 */
export function restartAppFresh(): void {
  try {
    if (isTranscludedHost()) {
      // The query lives in `__FOYER_SEARCH__`, not the URL, so clear it before
      // reloading or the bootloader's value would re-apply `?reset=1` forever.
      window.__FOYER_SEARCH__ = "";
      repairHostDocumentUrl();
      window.location.reload();
      return;
    }
    window.location.replace("/");
  } catch {
    /* see restartApp */
  }
}

/** Re-exported so callers need only this module for "restart" concerns. */
export { hostSearch };
